/**
 * Recording validation state for the discography page (WMC-565).
 *
 * Statuses come from the `validation_state {status, id}` the backend returns
 * on every recording (contract 4.7.2). Writes go through one injected
 * `ValidationWriter` — the page supplies it, so the store knows nothing about
 * the API, the client or refetching — and a row's status only changes here
 * once the writer reports the change applied.
 *
 * One tiny external store (useSyncExternalStore) so AG Grid cell renderers,
 * the toolbar and the floating bar can all react to a status change without
 * rebuilding the page's column definitions.
 */
import { useSyncExternalStore } from 'react';
import { RecordingValidationStateStatusEnum } from 'shared_components/generated/admin';
import type {
  AdminValidationStatus,
  ValidationApplyOutcome,
} from './validationBatch';

export type { AdminValidationStatus } from './validationBatch';

/** The contract's status set, as the generated client spells it. */
export type ValidationStatus = RecordingValidationStateStatusEnum;

export type ValidationState = { status: ValidationStatus; id: string | null };

export const STATUS_META: Record<
  ValidationStatus,
  { label: string; bg: string; hint: string }
> = {
  validated: {
    label: 'Validated',
    bg: 'tw-bg-westburyBlue',
    hint: 'Validated by the system',
  },
  pending: {
    label: 'Pending',
    bg: 'tw-bg-gray-500',
    hint: 'Not validated yet',
  },
  review: {
    label: 'Review',
    bg: 'tw-bg-westburyAmber',
    hint: 'Marked for review by an admin',
  },
  approved: {
    label: 'Approved',
    bg: 'tw-bg-westburyGreen',
    hint: 'Approved by an admin',
  },
  rejected: {
    label: 'Rejected',
    bg: 'tw-bg-westburyRed',
    hint: 'Rejected by an admin',
  },
};

/** Display order for counts and the column filter's option list. */
export const STATUS_ORDER: ValidationStatus[] = [
  'validated',
  'approved',
  'review',
  'rejected',
  'pending',
];

export const PENDING_STATE: ValidationState = { status: 'pending', id: null };

/** Writes `status` on `rowIds` (row id = recording id) and says what
 *  happened. Supplied by the page; see `applyValidationStatus`. */
export type ValidationWriter = (
  rowIds: string[],
  status: AdminValidationStatus
) => Promise<ValidationApplyOutcome>;

type StoreState = {
  /** Whether validation mode (selection + floating bar) is on. */
  mode: boolean;
  statuses: Record<string, ValidationState>;
  /** rowId -> the admin status currently being saved (spinner). */
  busy: Record<string, AdminValidationStatus>;
  /** Rows ticked in validation mode. */
  selected: Record<string, true>;
};

const initial = (): StoreState => ({
  mode: false,
  statuses: {},
  busy: {},
  selected: {},
});

let state: StoreState = initial();
let writer: ValidationWriter | null = null;
/** Bumped by `reset()`, so a write that resolves after the page moved on
 *  (another client, or unmounted) does not patch the fresh store. */
let generation = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const patch = (p: Partial<StoreState>) => {
  state = { ...state, ...p };
  emit();
};

const KNOWN_STATUSES: readonly string[] = Object.values(
  RecordingValidationStateStatusEnum
);
const isStatus = (v: unknown): v is ValidationStatus =>
  typeof v === 'string' && KNOWN_STATUSES.includes(v);

export const validationStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getState: () => state,

  /** The page installs the writer once it knows the API and the client. */
  setWriter(next: ValidationWriter | null) {
    writer = next;
  },

  /** Take the server's `validation_state` for each fetched row. A missing
   *  or unknown status reads as pending (contract 4.7.2). */
  seedFrom(
    entries: {
      id: string;
      state: { status?: string | null; id?: string | null } | null;
    }[]
  ) {
    const statuses = { ...state.statuses };
    let changed = false;
    entries.forEach(({ id, state: st }) => {
      const status = st?.status;
      const vid = st?.id;
      const next: ValidationState = {
        status: isStatus(status) ? status : 'pending',
        id: typeof vid === 'string' && vid ? vid : null,
      };
      const prev = statuses[id];
      if (prev && prev.status === next.status && prev.id === next.id) return;
      statuses[id] = next;
      changed = true;
    });
    if (changed) patch({ statuses });
  },

  setMode(mode: boolean) {
    patch({ mode, selected: mode ? state.selected : {} });
  },

  /**
   * Set `status` on `rowIds` through the writer. Rows already being saved
   * are skipped. Resolves with the writer's outcome, or null when there was
   * nothing to do. Only an applied write changes the statuses here and
   * clears the selection; the page's refetch then confirms it from the
   * server.
   */
  async setStatus(
    rowIds: string[],
    status: AdminValidationStatus
  ): Promise<ValidationApplyOutcome | null> {
    const ids = rowIds.filter((id) => !state.busy[id]);
    if (ids.length === 0 || !writer) return null;
    const busy = { ...state.busy };
    ids.forEach((id) => {
      busy[id] = status;
    });
    patch({ busy });

    const startedIn = generation;
    const outcome = await writer(ids, status);
    if (startedIn !== generation) return outcome;

    const nextBusy = { ...state.busy };
    ids.forEach((id) => {
      delete nextBusy[id];
    });
    if (outcome.kind !== 'applied') {
      patch({ busy: nextBusy });
      return outcome;
    }
    const statuses = { ...state.statuses };
    const selected = { ...state.selected };
    ids.forEach((id) => {
      statuses[id] = { status, id: statuses[id]?.id ?? null };
      delete selected[id];
    });
    patch({ statuses, busy: nextBusy, selected });
    return outcome;
  },

  toggleSelected(rowId: string) {
    const selected = { ...state.selected };
    if (selected[rowId]) delete selected[rowId];
    else selected[rowId] = true;
    patch({ selected });
  },
  setSelected(rowIds: string[]) {
    const selected: Record<string, true> = {};
    rowIds.forEach((id) => {
      selected[id] = true;
    });
    patch({ selected });
  },
  clearSelected() {
    patch({ selected: {} });
  },

  /** Back to a fresh store — for tests, for leaving the page and for
   *  switching client (statuses are per client, contract 4.7.3). */
  reset() {
    generation += 1;
    writer = null;
    state = initial();
    emit();
  },
};

export function useValidationStore<T>(selector: (s: StoreState) => T): T {
  return useSyncExternalStore(validationStore.subscribe, () => selector(state));
}

export const useValidationState = (rowId: string): ValidationState =>
  useValidationStore((s) => s.statuses[rowId]) ?? PENDING_STATE;

/** The rows among `rowIds` whose status is pending (unknown rows count as
 *  pending, like everywhere else). */
export const pendingRowIds = (
  statuses: Record<string, ValidationState>,
  rowIds: string[]
): string[] =>
  rowIds.filter((id) => (statuses[id] ?? PENDING_STATE).status === 'pending');

export const countStatuses = (
  statuses: Record<string, ValidationState>,
  rowIds?: string[]
): Record<ValidationStatus, number> => {
  const counts: Record<ValidationStatus, number> = {
    validated: 0,
    pending: 0,
    review: 0,
    approved: 0,
    rejected: 0,
  };
  const ids = rowIds ?? Object.keys(statuses);
  ids.forEach((id) => {
    counts[(statuses[id] ?? PENDING_STATE).status] += 1;
  });
  return counts;
};
